import React, {useEffect} from 'react'
import {Icon} from 'pepsico-ds'
import type {NavigateFunction} from 'react-router-dom'
import type {Dispatch, SetStateAction} from 'react'
import type {RightToolbarItem, SidebarItem} from './types'
import {LIGHT_THEME_LABEL, DARK_THEME_LABEL} from './constants'
import {canAccess, type AccessControlMap} from '@/services/accessControl/resolver'
import {
  SIDEBAR_ROUTES,
  HEADER_NAV_ROUTES,
  SIDEBAR_NAV_ROUTES,
  getActiveMenuId as resolveActiveMenu,
} from '@/routes/routeConfig'

interface BuildRightToolbarParams {
  profileMenuAnchorRef: React.RefObject<HTMLDivElement | null>
  getUserInitial: () => string
  toggleProfileMenu: () => void
}

export const useProfileMenuHotkeys = (isOpen: boolean, onClose: () => void) => {
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])
}

export const useSyncTheme = (
  isDark: boolean,
  setPepsicoTheme: (theme: typeof LIGHT_THEME_LABEL | typeof DARK_THEME_LABEL) => void,
) => {
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark)

    const pepsicoTheme = isDark ? DARK_THEME_LABEL : LIGHT_THEME_LABEL
    setPepsicoTheme(pepsicoTheme)
  }, [isDark, setPepsicoTheme])
}

export const useActiveMenu = (pathname: string, setActiveMenuId: Dispatch<SetStateAction<string>>) => {
  useEffect(() => {
    setActiveMenuId(resolveActiveMenu(pathname))
  }, [pathname, setActiveMenuId])
}

export const buildRightToolbar = ({
  profileMenuAnchorRef,
  getUserInitial,
  toggleProfileMenu,
}: BuildRightToolbarParams): RightToolbarItem[] => [
  {
    icon: React.createElement(
      'div',
      {className: 'ml-2 flex cursor-pointer items-center select-none'},
      React.createElement(
        'div',
        {
          ref: profileMenuAnchorRef,
          className:
            'flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#838383] text-[14px] leading-none font-bold text-[#FFFFFF]',
          style: {fontFamily: 'Inter'},
        },
        getUserInitial(),
      ),
      React.createElement(Icon, {icon: 'arrow_drop_down', size: 'large', className: 'text-white'}),
    ),
    onClickIcon: toggleProfileMenu,
  },
]

interface CreateSidebarHandlersParams {
  navigate: NavigateFunction
  setActiveMenuId: Dispatch<SetStateAction<string>>
  sidebarItems: SidebarItem[]
}

export const createSidebarHandlers = ({navigate, setActiveMenuId, sidebarItems}: CreateSidebarHandlersParams) => {
  const handleSidebarItemSelect = (item: string | SidebarItem) => {
    const itemId = typeof item === 'string' ? item : item.id
    setActiveMenuId(itemId)

    if (typeof item === 'object' && item.path) {
      navigate(item.path)
      return
    }

    const selectedItem = sidebarItems.find(menuItem => menuItem.id === itemId)
    if (selectedItem?.url) {
      navigate(selectedItem.url)
    } else if (itemId === 'dashboard') {
      navigate('/')
    }
  }

  return {handleSidebarItemSelect}
}

/**
 * Get sidebar items based on GrowthBook ACL.
 * Derives the full list from centralized SIDEBAR_ROUTES and checks each nav slug.
 * @param userRoles Array of user role strings from JWT
 * @param acl AccessControlMap from GrowthBook emd-access-control feature
 * @returns Filtered sidebar items
 */
export const getSidebarItems = (userRoles: string[], acl: AccessControlMap): SidebarItem[] => {
  return SIDEBAR_ROUTES.filter(
    route => route.requiresAccess === false || canAccess(route.pageSlug, userRoles, acl),
  ).map(route => ({
    id: route.id,
    label: route.label ?? route.id,
    icon: route.icon ?? '',
    url: route.path,
  }))
}

/**
 * Get the default route based on GrowthBook ACL.
 * Returns the URL of the first available sidebar item for the user.
 * Falls back to '/' if no sidebar items are accessible.
 * @param userRoles Array of user role strings from JWT
 * @param acl AccessControlMap from GrowthBook emd-access-control feature
 * @returns The default route URL for the user
 */
export const getDefaultRoute = (userRoles: string[], acl: AccessControlMap): string => {
  const availableItems = getSidebarItems(userRoles, acl)
  if (availableItems.length > 0) {
    return availableItems[0].url || '/'
  }
  return '/'
}

/**
 * Get header navigation items (shown in top header tabs) based on GrowthBook ACL.
 */
export const getHeaderNavItems = (userRoles: string[], acl: AccessControlMap): SidebarItem[] => {
  return HEADER_NAV_ROUTES.filter(route => canAccess(route.pageSlug, userRoles, acl)).map(route => ({
    id: route.id,
    label: route.label ?? route.id,
    icon: route.icon ?? '',
    url: route.path,
  }))
}

/**
 * Get sidebar-only navigation items (shown in left sidebar) based on GrowthBook ACL.
 */
export const getSidebarOnlyItems = (userRoles: string[], acl: AccessControlMap): SidebarItem[] => {
  return SIDEBAR_NAV_ROUTES.filter(route => canAccess(route.pageSlug, userRoles, acl)).map(route => ({
    id: route.id,
    label: route.label ?? route.id,
    icon: route.icon ?? '',
    url: route.path,
  }))
}
